/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Agent Execution Management
 * 
 * Handles agent execution with streaming, SSE event processing, and execution state management.
 */

import { QueryClient } from '@tanstack/react-query';
import { executeAgentWithStreaming, getSessionExecutionStatus } from '@shared/lib/api/streaming';
import { terminateAgentExecution } from '@shared/lib/api/review';
import { AgentExecutionStreamEvent } from '@shared/types/responses/streaming';
import { ChatState, ChatAction, ChatMessage, AgentContextProvider } from './types';
import { SessionManager } from './session-manager';
import { MessageManager } from './message-manager';

export class ExecutionManager {
  constructor(
    private dispatch: React.Dispatch<ChatAction>,
    private queryClient: QueryClient,
    private sessionManager: SessionManager,
    private messageManager: MessageManager,
    private user: any,
    private contextProviderRef: React.MutableRefObject<AgentContextProvider | undefined>
  ) {}

  /**
   * Update execution status based on server state using React Query
   */
  updateExecutionStatusFromServer(sessionId: number, state: ChatState): void {
    // Use React Query to fetch execution status
    this.queryClient.fetchQuery(getSessionExecutionStatus(sessionId))
      .then((data) => {
        // Update local state to match server state
        if (state.isExecuting !== data.isExecuting) {
          this.dispatch({ type: 'SET_EXECUTING', payload: data.isExecuting });
        }
      })
      .catch((error) => {
        console.error('Error checking execution status:', error);
        // On error, assume no execution to prevent stuck state
        if (state.isExecuting) {
          this.dispatch({ type: 'SET_EXECUTING', payload: false });
        }
      });
  }

  /**
   * Execute agent with streaming and real-time UI updates
   */
  async executeAgentWithStreaming(
    agentName: string, 
    message: string, 
    fileAttachment: File | undefined,
    state: ChatState,
    setIsLoading: (loading: boolean) => void,
    titleGenerationTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ): Promise<string> {

    // Set executing state immediately (will be validated against server state)
    this.dispatch({ type: 'SET_EXECUTING', payload: true });

    return new Promise(async (resolve, reject) => {
      let savedAssistantMessage: any = null; // Declare in Promise scope for cleanup access
      
      try {
        // Ensure we have a session before proceeding (lazy creation)
        const sessionId = await this.sessionManager.ensureSession(state);

        // Add user message immediately
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: fileAttachment 
            ? `${message}\n\n📎 Attached: ${fileAttachment.name}`
            : message,
          timestamp: new Date()
        };
        await this.messageManager.addMessage(userMessage, state, sessionId);

        // Create assistant message BEFORE execution to get ID for SSE events
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: 'Thinking...',
          timestamp: new Date(),
          isStreaming: true,
          toolCalls: [],
          entityLinks: [],
          subAgentCalls: []
        };
        savedAssistantMessage = await this.messageManager.addMessage(assistantMessage, state, sessionId);
        
        // Get assistant message ID for SSE events
        const assistantMessageId = savedAssistantMessage?.uuid || savedAssistantMessage?.id?.toString();
        if (!assistantMessageId) {
          throw new Error('Failed to get message ID from database');
        }

        // Set loading state
        setIsLoading(true);

        const handleStreamEvent = (event: AgentExecutionStreamEvent) => {
          switch (event.type) {
            case 'execution_started':
              // Store execution ID for potential termination
              console.log('🎯 Execution started - storing execution ID:', event.data.streamId);
              this.dispatch({
                type: 'SET_EXECUTION_STATE',
                payload: {
                  executionId: event.data.streamId,
                  canTerminate: true
                }
              });
              break;

            case 'tool_call_started':
              this.dispatch({
                type: 'ADD_TOOL_CALL',
                payload: {
                  messageId: assistantMessageId,
                  toolCall: {
                    id: event.data.id,
                    toolName: event.data.toolName,
                    input: event.data.input,
                    success: false // Initially false until completed
                  }
                }
              });
              break;

            case 'tool_call_completed':
              this.dispatch({
                type: 'UPDATE_TOOL_CALL',
                payload: {
                  messageId: assistantMessageId,
                  toolCallId: event.data.id,
                  updates: {
                    output: event.data.result,
                    success: event.data.success,
                    executionTime: event.data.executionTime,
                    error: event.data.error
                  }
                }
              });
              // Refresh plan immediately when plan_manager tool completes successfully
              if (event.data.toolName === 'plan_manager' && event.data.success && sessionId) {
                this.sessionManager.refreshSessionPlan(sessionId);
              }
              break;

            case 'agent_dispatch_started':
              this.dispatch({
                type: 'ADD_SUB_AGENT_CALL',
                payload: {
                  messageId: assistantMessageId,
                  subAgentCall: {
                    agentName: event.data.targetAgent,
                    query: event.data.query,
                    status: 'started',
                    timestamp: new Date()
                  }
                }
              });
              break;

            case 'agent_dispatch_completed':
              this.dispatch({
                type: 'UPDATE_SUB_AGENT_CALL',
                payload: {
                  messageId: assistantMessageId,
                  agentName: event.data.targetAgent,
                  updates: {
                    response: event.data.response,
                    status: event.data.status === 'failed' ? 'failed' : 'completed'
                  }
                }
              });
              break;

            case 'entity_created':
            case 'entity_updated':
              this.dispatch({
                type: 'ADD_ENTITY_LINK',
                payload: {
                  messageId: assistantMessageId,
                  entityLink: {
                    type: event.data.type,
                    id: event.data.id,
                    name: event.data.name,
                    operation: event.data.operation,
                    // Include optional fields for product entities
                    ...(event.data.identifier && { identifier: event.data.identifier }),
                    ...(event.data.subtype && { subtype: event.data.subtype }),
                    ...(event.data.issueType && { issueType: event.data.issueType })
                  }
                }
              });
              break;

            case 'partial_response':
              this.dispatch({
                type: 'UPDATE_MESSAGE_CONTENT',
                payload: {
                  messageId: assistantMessageId,
                  content: event.data.content,
                  isIncremental: event.data.isIncremental
                }
              });
              break;

            case 'execution_completed':
              const result = event.data.result;
              this.dispatch({ 
                type: 'UPDATE_MESSAGE', 
                payload: {
                  messageId: assistantMessageId,
                  updates: {
                    isStreaming: false,
                    content: result?.response || result?.text || result || '',
                    metadata: result?.metadata || {}
                  }
                }
              });
              // Clear execution state
              this.dispatch({
                type: 'SET_EXECUTION_STATE',
                payload: {
                  executionId: undefined,
                  canTerminate: false
                }
              });
              setIsLoading(false);
              // Execution state will be updated by server check when message is no longer "Thinking..."
              resolve(result?.response || '');
              break;

            case 'execution_error':
              this.dispatch({
                type: 'UPDATE_MESSAGE',
                payload: {
                  messageId: assistantMessageId,
                  updates: {
                    isStreaming: false,
                    content: `Error: ${event.data.error}`
                  }
                }
              });
              
              // Mark all pending sub-agent calls as failed when execution is terminated
              this.dispatch({
                type: 'UPDATE_ALL_SUB_AGENT_CALLS',
                payload: {
                  messageId: assistantMessageId,
                  updates: {
                    status: 'failed'
                  }
                }
              });
              
              // Clear execution state
              this.dispatch({
                type: 'SET_EXECUTION_STATE',
                payload: {
                  executionId: undefined,
                  canTerminate: false
                }
              });
              setIsLoading(false);
              // Execution state will be updated by server check when message is no longer "Thinking..."
              reject(new Error(event.data.error));
              break;
          }
        };

        const handleStreamError = (error: Error) => {
          console.error('❌ Streaming error:', error);
          
          // Update assistant message with error content
          this.dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              messageId: assistantMessageId,
              updates: {
                isStreaming: false,
                content: 'Sorry, I encountered an error processing your request. Please try again.'
              }
            }
          });
          
          // Clear execution state
          this.dispatch({
            type: 'SET_EXECUTION_STATE',
            payload: {
              executionId: undefined,
              canTerminate: false
            }
          });
          
          setIsLoading(false);
          // Execution state will be updated by server check
          reject(error);
        };

        const handleStreamComplete = async () => {
          setIsLoading(false);
          // Execution state will be updated by server check when message is finalized
          
          // Refresh session plan after execution completes
          // This picks up any plan changes made by tools like plan_manager
          if (sessionId) {
            await this.sessionManager.refreshSessionPlan(sessionId);
          }
        };

        // Get dynamic context from provider
        const dynamicContext = this.contextProviderRef.current?.() ?? {};
        
        // Log context for debugging
        if (Object.keys(dynamicContext).length > 0) {
          console.log('🎯 Executing agent with context:', dynamicContext);
        }
        
        // Prepare request
        const request = {
          message,
          userId: this.user?.id, // Include user ID for UserContext building
          context: dynamicContext, // Include dynamic context from provider
          ...(fileAttachment && {
            fileAttachment: {
              filename: fileAttachment.name,
              fileBuffer: Array.from(new Uint8Array(await fileAttachment.arrayBuffer())), // Convert ArrayBuffer to array for JSON serialization
              mimeType: fileAttachment.type
            }
          })
        };

        // Schedule title generation when execution starts
        this.sessionManager.scheduleSessionTitleGeneration(sessionId, state, titleGenerationTimeoutRef);

        // Execute streaming request with chat session information
        const abortController = await executeAgentWithStreaming(agentName, {
          ...request,
          chatSessionId: sessionId,
          chatMessageId: savedAssistantMessage?.id // Use database ID for server-side updates
        }, {
          onEvent: handleStreamEvent,
          onError: handleStreamError,
          onComplete: handleStreamComplete,
          onExecutionStart: (executionData) => {
            // Capture execution ID immediately when execution starts
            console.log('🚀 Execution started via callback:', executionData.executionId);
            this.dispatch({
              type: 'SET_EXECUTION_STATE',
              payload: {
                executionId: executionData.executionId,
                canTerminate: true
              }
            });
          }
        }).catch(handleStreamError);
        
        // Store abort controller for potential termination
        // Note: The execution ID is set both via callback and SSE events for redundancy
        
      } catch (error) {
        console.error('❌ Error during execution setup:', error);
        
        // If we created an assistant message but execution failed to start, remove it
        if (savedAssistantMessage) {
          this.dispatch({
            type: 'SET_MESSAGES',
            payload: state.messages.filter(m => 
              !(m.uuid === savedAssistantMessage.uuid && m.content === 'Thinking...')
            )
          });
        }
        
        // Clean up loading state - execution state will be updated by server check
        setIsLoading(false);
        reject(error);
      }
    });
  }

  /**
   * Terminate the current execution
   */
  async terminateCurrentExecution(executionId: string, reason: string = 'User requested termination'): Promise<boolean> {
    try {
      
      console.log('🛑 Attempting to terminate execution:', {
        executionId,
        reason,
        timestamp: new Date().toISOString()
      });
      
      const result = await terminateAgentExecution(executionId, reason);
      
      console.log('📋 Termination API response:', result);
      
      if (result.terminated) {
        // Clear execution state
        this.dispatch({
          type: 'SET_EXECUTION_STATE',
          payload: {
            executionId: undefined,
            canTerminate: false
          }
        });
        
        console.log('✅ Execution terminated successfully:', executionId);
        return true;
      } else {
        console.warn('⚠️ Termination API returned false:', result);
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to terminate execution:', {
        executionId,
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      });
      return false;
    }
  }
}
